import type { BenefitCategory, BenefitSummary, BenefitTag, BenefitsData } from "@yutai/shared"
import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import useSWR from "swr"
import type { AllocationFrequency } from "../../lib/allocationFrequency.js"
import type { MarketCapTier } from "../../lib/marketCapTier.js"
import type { RelevanceLevel } from "../../lib/relevance.js"
import { computeFilterCounts, filterBenefits, sortBenefits } from "./filterBenefits.js"

const R2_URL = import.meta.env.VITE_R2_PUBLIC_URL ?? ""
const BENEFITS_URL = import.meta.env.DEV ? `${R2_URL}/benefits.json` : "/benefits.json"

async function fetcher(url: string): Promise<BenefitSummary[]> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`)
  const data: BenefitsData = await res.json()
  return data.items.map((item) => ({
    ...item,
    tags: item.tags ?? [],
    deliveryMonths: item.deliveryMonths ?? [],
    holdingYears: item.holdingYears ?? [],
    minimumBenefitValue: item.minimumBenefitValue ?? null,
    tiers: item.tiers ?? [],
    tiersNeedDescription: item.tiersNeedDescription ?? false,
    title: item.title ?? "",
    businessDescription: item.businessDescription ?? "",
    benefitRelevanceScore: item.benefitRelevanceScore ?? 0,
    approxStockPrice: item.approxStockPrice ?? null,
    marketCap: item.marketCap ?? null,
    dividendYield: item.dividendYield ?? null,
    benefitYield: item.benefitYield ?? null,
    financialsUpdatedAt: item.financialsUpdatedAt ?? null,
  }))
}

export type SortKey =
  | "code"
  | "benefitValue"
  | "combinedYield"
  | "dividendYield"
  | "benefitYield"
  | "marketCap"
  | "acquisitionAmount"
  | "recordDate"
  | "lastChanged"

const SORT_KEYS: SortKey[] = [
  "code",
  "benefitValue",
  "combinedYield",
  "dividendYield",
  "benefitYield",
  "marketCap",
  "acquisitionAmount",
  "recordDate",
  "lastChanged",
]
export type SortOrder = "asc" | "desc"

export interface Filters {
  query: string
  categories: BenefitCategory[]
  tags: BenefitTag[]
  months: number[]
  deliveryMonths: number[]
  industries: string[]
  minBenefitValue: number | null
  longTermBonus: number | null
  relevance: RelevanceLevel[]
  minDividendYield: number | null
  minBenefitYield: number | null
  minCombinedYield: number | null
  capTiers: MarketCapTier[]
  allocationFrequency: AllocationFrequency[]
  maxAcquisitionAmount: number | null
  sort: SortKey
  order: SortOrder
}

export const DEFAULT_FILTERS: Filters = {
  query: "",
  categories: [],
  tags: [],
  months: [],
  deliveryMonths: [],
  industries: [],
  minBenefitValue: null,
  longTermBonus: null,
  relevance: [],
  minDividendYield: null,
  minBenefitYield: null,
  minCombinedYield: null,
  capTiers: [],
  allocationFrequency: [],
  maxAcquisitionAmount: null,
  sort: "code",
  order: "asc",
}

const CAP_TIERS: MarketCapTier[] = ["small", "mid", "large"]

const ALLOCATION_FREQUENCIES: AllocationFrequency[] = ["once", "twice", "thriceOrMore"]

const RELEVANCE_LEVELS: RelevanceLevel[] = ["low", "mid", "high"]

const PAGE_SIZE = 20

function parseFilters(searchParams: URLSearchParams): Filters {
  return {
    query: searchParams.get("q") ?? "",
    categories: searchParams.getAll("cat") as BenefitCategory[],
    tags: searchParams.getAll("tag") as BenefitTag[],
    months: searchParams.getAll("month").map(Number).filter(Boolean),
    deliveryMonths: searchParams.getAll("deliveryMonth").map(Number).filter(Boolean),
    industries: searchParams.getAll("industry"),
    minBenefitValue: searchParams.get("minValue") ? Number(searchParams.get("minValue")) : null,
    longTermBonus: searchParams.get("longTerm") ? Number(searchParams.get("longTerm")) : null,
    relevance: searchParams
      .getAll("relevance")
      .filter((v): v is RelevanceLevel => RELEVANCE_LEVELS.includes(v as RelevanceLevel)),
    minDividendYield: searchParams.get("minDivYield")
      ? Number(searchParams.get("minDivYield"))
      : null,
    minBenefitYield: searchParams.get("minBenYield")
      ? Number(searchParams.get("minBenYield"))
      : null,
    minCombinedYield: searchParams.get("minYield") ? Number(searchParams.get("minYield")) : null,
    capTiers: searchParams
      .getAll("capTier")
      .filter((v): v is MarketCapTier => CAP_TIERS.includes(v as MarketCapTier)),
    allocationFrequency: searchParams
      .getAll("allocFreq")
      .filter((v): v is AllocationFrequency =>
        ALLOCATION_FREQUENCIES.includes(v as AllocationFrequency)
      ),
    maxAcquisitionAmount: searchParams.get("maxAmount")
      ? Number(searchParams.get("maxAmount"))
      : null,
    sort: SORT_KEYS.includes(searchParams.get("sort") as SortKey)
      ? (searchParams.get("sort") as SortKey)
      : "code",
    order: (searchParams.get("order") as SortOrder) || "asc",
  }
}

export function useBenefits() {
  const { data, error, isLoading } = useSWR<BenefitSummary[]>(BENEFITS_URL, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 3600_000,
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const filters = parseFilters(searchParams)
  const page = Math.max(1, Number(searchParams.get("page") ?? "1"))

  const filtered = useMemo(() => {
    if (!data) return []
    const f = parseFilters(searchParams)
    return sortBenefits(filterBenefits(data, f), f.sort, f.order)
  }, [data, searchParams])

  const filterCounts = useMemo(() => {
    if (!data) return null
    return computeFilterCounts(data, parseFilters(searchParams))
  }, [data, searchParams])

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  function updateFilters(partial: Partial<Filters>) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("page")

        if ("query" in partial) {
          if (partial.query) next.set("q", partial.query)
          else next.delete("q")
        }
        if ("categories" in partial) {
          next.delete("cat")
          for (const c of partial.categories ?? []) next.append("cat", c)
        }
        if ("tags" in partial) {
          next.delete("tag")
          for (const t of partial.tags ?? []) next.append("tag", t)
        }
        if ("months" in partial) {
          next.delete("month")
          for (const m of partial.months ?? []) next.append("month", String(m))
        }
        if ("deliveryMonths" in partial) {
          next.delete("deliveryMonth")
          for (const m of partial.deliveryMonths ?? []) next.append("deliveryMonth", String(m))
        }
        if ("industries" in partial) {
          next.delete("industry")
          for (const i of partial.industries ?? []) next.append("industry", i)
        }
        if ("minBenefitValue" in partial) {
          if (partial.minBenefitValue != null) next.set("minValue", String(partial.minBenefitValue))
          else next.delete("minValue")
        }
        if ("longTermBonus" in partial) {
          if (partial.longTermBonus != null) next.set("longTerm", String(partial.longTermBonus))
          else next.delete("longTerm")
        }
        if ("relevance" in partial) {
          next.delete("relevance")
          for (const r of partial.relevance ?? []) next.append("relevance", r)
        }
        if ("minDividendYield" in partial) {
          if (partial.minDividendYield != null)
            next.set("minDivYield", String(partial.minDividendYield))
          else next.delete("minDivYield")
        }
        if ("minBenefitYield" in partial) {
          if (partial.minBenefitYield != null)
            next.set("minBenYield", String(partial.minBenefitYield))
          else next.delete("minBenYield")
        }
        if ("minCombinedYield" in partial) {
          if (partial.minCombinedYield != null)
            next.set("minYield", String(partial.minCombinedYield))
          else next.delete("minYield")
        }
        if ("capTiers" in partial) {
          next.delete("capTier")
          for (const t of partial.capTiers ?? []) next.append("capTier", t)
        }
        if ("allocationFrequency" in partial) {
          next.delete("allocFreq")
          for (const f of partial.allocationFrequency ?? []) next.append("allocFreq", f)
        }
        if ("maxAcquisitionAmount" in partial) {
          if (partial.maxAcquisitionAmount != null)
            next.set("maxAmount", String(partial.maxAcquisitionAmount))
          else next.delete("maxAmount")
        }
        if ("sort" in partial) {
          if (partial.sort && partial.sort !== "code") next.set("sort", partial.sort)
          else next.delete("sort")
        }
        if ("order" in partial) {
          if (partial.order && partial.order !== "asc") next.set("order", partial.order)
          else next.delete("order")
        }

        return next
      },
      { replace: true }
    )
  }

  return {
    items: paged,
    total: filtered.length,
    totalAll: data?.length ?? 0,
    page,
    totalPages,
    filters,
    filterCounts,
    updateFilters,
    isLoading,
    error,
  }
}
